import React from 'react';
import { Box, Container, Typography, Paper, Grid, Chip, Stack, Divider } from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import DateRangeIcon from '@mui/icons-material/DateRange';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const Experience = () => {
  const { t } = useTranslation();

  const experiences = [
    {
      company: t('experience.positions.bilgeadam.company'),
      title: t('experience.positions.bilgeadam.title'),
      location: t('experience.positions.bilgeadam.location'),
      period: t('experience.positions.bilgeadam.period'),
      description: t('experience.positions.bilgeadam.descriptions', { returnObjects: true }),
      skills: ["Spring Boot", "Microservices", "Java", "Software Design Patterns", "Junit", "Apache Kafka", "gRPC"]
    },
    {
      company: t('experience.positions.innova.company'),
      location: t('experience.positions.innova.location'),
      period: t('experience.positions.innova.period'),
      positions: [
        {
          title: t('experience.positions.innova.senior_consultant.title'),
          period: t('experience.positions.innova.senior_consultant.period'),
          description: t('experience.positions.innova.senior_consultant.descriptions', { returnObjects: true }),
          skills: ["Spring Boot", "Microservices", "Java", "Reactive Programming", "MongoDB", "Docker", "Oracle Database", "React.js", "TypeScript", "Kubernetes"]
        },
        {
          title: t('experience.positions.innova.consultant.title'),
          period: t('experience.positions.innova.consultant.period'),
          description: t('experience.positions.innova.consultant.descriptions', { returnObjects: true }),
          skills: ["Microservices", "Java", "Software Design Patterns", "MongoDB", "Docker", "React.js", "Apache Kafka"]
        },
        {
          title: t('experience.positions.innova.senior_specialist.title'),
          period: t('experience.positions.innova.senior_specialist.period'),
          description: t('experience.positions.innova.senior_specialist.descriptions', { returnObjects: true }),
          skills: ["Java", "Microservices", "Docker", "Kubernetes", "MongoDB"]
        },
        {
          title: t('experience.positions.innova.specialist.title'),
          period: t('experience.positions.innova.specialist.period'),
          description: t('experience.positions.innova.specialist.descriptions', { returnObjects: true }),
          skills: ["Java", "Microservices", "React.js", "TypeScript", "MongoDB"]
        }
      ]
    },
    {
      company: t('experience.positions.freelance.company'),
      title: t('experience.positions.freelance.title'),
      location: t('experience.positions.freelance.location'),
      period: t('experience.positions.freelance.period'),
      description: t('experience.positions.freelance.descriptions', { returnObjects: true }),
      skills: ["PHP", "HTML", "Laravel", "jQuery", "MySQL", "Java", "Gradle"]
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5
      }
    }
  };

  const chipVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.3
      }
    }
  };

  const renderRegularExperience = (exp, index) => (
    <Paper
      elevation={0}
      component={motion.div}
      variants={itemVariants}
      sx={{
        p: 4,
        background: 'rgba(144, 202, 249, 0.08)',
        borderRadius: 2,
        transition: 'all 0.3s ease',
        '&:hover': {
          background: 'rgba(144, 202, 249, 0.15)',
          transform: 'translateY(-2px)'
        }
      }}
    >
      <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{ once: true }}>
        <motion.div variants={itemVariants}>
          <Typography variant="h5" color="primary" gutterBottom>
            {exp.title}
          </Typography>
        </motion.div>
        <motion.div variants={itemVariants}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <BusinessIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6" color="primary">
              {exp.company}
            </Typography>
          </Box>
        </motion.div>
        <motion.div variants={itemVariants}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <DateRangeIcon sx={{ mr: 1, color: 'text.secondary' }} />
            <Typography variant="body1" color="text.secondary">
              {exp.period}
            </Typography>
          </Box>
        </motion.div>
        <motion.div variants={itemVariants}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <LocationOnIcon sx={{ mr: 1, color: 'text.secondary' }} />
            <Typography variant="body1" color="text.secondary">
              {exp.location}
            </Typography>
          </Box>
        </motion.div>
        <Box component="ul" sx={{ m: 0, pl: 2, mb: 2 }}>
          {exp.description.map((desc, i) => (
            <motion.li
              key={i}
              variants={itemVariants}
              style={{ listStyle: 'none' }}
            >
              <Typography variant="body1" sx={{ mb: 1 }}>
                {desc}
              </Typography>
            </motion.li>
          ))}
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
          {exp.skills.map((skill, i) => (
            <motion.div
              key={i}
              variants={chipVariants}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Chip
                label={skill}
                size="small"
                sx={{
                  m: 0.5,
                  bgcolor: 'rgba(144, 202, 249, 0.08)',
                  color: 'text.secondary',
                  '&:hover': {
                    bgcolor: 'rgba(144, 202, 249, 0.15)',
                  }
                }}
              />
            </motion.div>
          ))}
        </Stack>
      </motion.div>
    </Paper>
  );

  const renderInnovaExperience = (exp, index) => (
    <Paper
      elevation={0}
      component={motion.div}
      variants={itemVariants}
      sx={{
        p: 4,
        background: 'rgba(144, 202, 249, 0.08)',
        borderRadius: 2,
        transition: 'all 0.3s ease',
        '&:hover': {
          background: 'rgba(144, 202, 249, 0.15)',
          transform: 'translateY(-2px)'
        }
      }}
    >
      <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{ once: true }}>
        <motion.div variants={itemVariants}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <BusinessIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h5" color="primary">
              {exp.company}
            </Typography>
          </Box>
        </motion.div>
        <motion.div variants={itemVariants}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <DateRangeIcon sx={{ mr: 1, color: 'text.secondary' }} />
            <Typography variant="body1" color="text.secondary">
              {exp.period}
            </Typography>
          </Box>
        </motion.div>
        <motion.div variants={itemVariants}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
            <LocationOnIcon sx={{ mr: 1, color: 'text.secondary' }} />
            <Typography variant="body1" color="text.secondary">
              {exp.location}
            </Typography>
          </Box>
        </motion.div>

        {exp.positions.map((position, i) => (
          <motion.div
            key={i}
            variants={itemVariants}
            sx={{ mb: i !== exp.positions.length - 1 ? 4 : 0 }}
          >
            <motion.div variants={itemVariants}>
              <Typography variant="h6" color="primary" gutterBottom>
                {position.title}
              </Typography>
            </motion.div>
            <motion.div variants={itemVariants}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {position.period}
              </Typography>
            </motion.div>
            <Box component="ul" sx={{ m: 0, pl: 2, mb: 2 }}>
              {position.description.map((desc, j) => (
                <motion.li
                  key={j}
                  variants={itemVariants}
                  style={{ listStyle: 'none' }}
                >
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    {desc}
                  </Typography>
                </motion.li>
              ))}
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap" gap={1} sx={{ mb: 3 }}>
              {position.skills.map((skill, j) => (
                <motion.div
                  key={j}
                  variants={chipVariants}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Chip
                    label={skill}
                    size="small"
                    sx={{
                      m: 0.5,
                      bgcolor: 'rgba(144, 202, 249, 0.08)',
                      color: 'text.secondary',
                      '&:hover': {
                        bgcolor: 'rgba(144, 202, 249, 0.15)',
                      }
                    }}
                  />
                </motion.div>
              ))}
            </Stack>
            {i !== exp.positions.length - 1 && (
              <motion.div variants={itemVariants}>
                <Divider sx={{ my: 3, borderColor: 'rgba(144, 202, 249, 0.1)' }} />
              </motion.div>
            )}
          </motion.div>
        ))}
      </motion.div>
    </Paper>
  );

  return (
    <Box
      id="experience"
      sx={{
        py: 12,
        background: 'linear-gradient(180deg, #020c1b 0%, #0a192f 100%)',
      }}
    >
      <Container>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <Typography variant="h2" color="primary" sx={{ mb: 6 }}>
            {t('experience.title')}
          </Typography>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <Grid container spacing={4}>
            {experiences.map((exp, index) => (
              <Grid item xs={12} key={index}>
                {exp.positions ? renderInnovaExperience(exp, index) : renderRegularExperience(exp, index)}
              </Grid>
            ))}
          </Grid>
        </motion.div>
      </Container>
    </Box>
  );
};

export default Experience;
